import { useState, useRef, useEffect } from "react";
import {
  resetCounterProgress,
  resetCounterTimer,
  removeCounterById,
  stampLastSelected,
} from "../utils/counterState";

export default function useCounterActions({
  counters,
  activeCounterId,
  setCounters,
  setActiveCounterId,
  setIsRunning,
}) {
  // -------------------------
  // State
  // -------------------------
  const [resetType, setResetType] = useState(null);
  const [counterToDelete, setCounterToDelete] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [undoPayload, setUndoPayload] = useState(null);

  const undoTimeoutRef = useRef(null);

  // -------------------------
  // Cleanup
  // -------------------------
  useEffect(() => {
    return () => clearTimeout(undoTimeoutRef.current);
  }, []);

  // -------------------------
  // Helpers
  // -------------------------
  const createSnapshot = () => ({
    counters: JSON.parse(JSON.stringify(counters)),
    activeCounterId,
  });

  const showUndoToast = (message, snapshot = null) => {
    clearTimeout(undoTimeoutRef.current);

    setUndoPayload({ message, snapshot });

    undoTimeoutRef.current = setTimeout(() => {
      setUndoPayload(null);
    }, 5000);
  };

  const closeModal = () => {
    setShowModal(false);
    setResetType(null);
    setCounterToDelete(null);
  };

  // -------------------------
  // Actions
  // -------------------------
  const handleDeleteCounterClick = (id) => {
    if (counters.length === 1) {
      showUndoToast("You must keep at least one counter.");
      return;
    }

    setResetType("delete");
    setCounterToDelete(id);
    setShowModal(true);
  };

  const handleCountResetClick = (activeCounter) => {
    if (!activeCounter || activeCounter.isDisabled || activeCounter.currentCount < 1) {
      return;
    }

    setResetType("counter");
    setShowModal(true);
    setIsRunning(false);
  };

  const handleTimerResetClick = () => {
    setResetType("timer");
    setShowModal(true);
    setIsRunning(false);
  };

  const cancelReset = closeModal;

  // -------------------------
  // Core Handlers
  // -------------------------
  const handleDelete = (snapshot) => {
    const index = counters.findIndex((c) => c.id === counterToDelete);

    let newCounters = removeCounterById(counters, counterToDelete);

    if (counterToDelete === activeCounterId) {
      if (newCounters.length > 0) {
        const nextCounter =
          newCounters[index] || newCounters[index - 1];

        const nextId = nextCounter?.id || null;

        setActiveCounterId(nextId);

        if (nextId) {
          newCounters = stampLastSelected(newCounters, nextId);
        }
      } else {
        setActiveCounterId(null);
      }
    }

    setCounters(newCounters);
    showUndoToast("Counter deleted", snapshot);
  };

  const handleCounterReset = (snapshot) => {
    setCounters((prev) =>
      resetCounterProgress(prev, activeCounterId)
    );

    showUndoToast("Counter reset", snapshot);
  };

  const handleTimerReset = (snapshot) => {
    setCounters((prev) =>
      resetCounterTimer(prev, activeCounterId)
    );

    setIsRunning(false);
    showUndoToast("Timer reset", snapshot);
  };

  const confirmReset = () => {
    const snapshot = createSnapshot();

    switch (resetType) {
      case "delete":
        handleDelete(snapshot);
        break;

      case "counter":
        handleCounterReset(snapshot);
        break;

      case "timer":
        handleTimerReset(snapshot);
        break;

      default:
        break;
    }

    closeModal();
  };

  // -------------------------
  // Undo
  // -------------------------
  const handleUndo = () => {
    if (!undoPayload?.snapshot) return;

    clearTimeout(undoTimeoutRef.current);

    setCounters(undoPayload.snapshot.counters);
    setActiveCounterId(undoPayload.snapshot.activeCounterId);

    setUndoPayload(null);
  };

  // -------------------------
  // API
  // -------------------------
  return {
    resetType,
    showModal,
    undoPayload,
    handleDeleteCounterClick,
    handleCountResetClick,
    handleTimerResetClick,
    cancelReset,
    confirmReset,
    handleUndo,
  };
}